export type PyExpr =
  | { type: 'Name'; id: string }
  | { type: 'Attribute'; value: PyExpr; attr: string }
  | { type: 'Call'; func: PyExpr; args: PyExpr[]; keywords?: PyKeyword[] }
  | { type: 'Constant'; value: string | number | boolean | null }
  | { type: 'Dict'; items: { key: PyExpr; value: PyExpr }[] }
  | { type: 'List'; items: PyExpr[] }
  | { type: 'Tuple'; items: PyExpr[] }
  | { type: 'Subscript'; value: PyExpr; index: PyExpr }
  | { type: 'BinOp'; left: PyExpr; op: string; right: PyExpr }
  | { type: 'Compare'; left: PyExpr; op: string; right: PyExpr };

export type PyStmt =
  | { type: 'Import'; names: { name: string; as?: string }[] }
  | { type: 'Assign'; targets: PyExpr[]; value: PyExpr }
  | { type: 'AugAssign'; target: PyExpr; op: string; value: PyExpr }
  | { type: 'Expr'; value: PyExpr }
  | { type: 'For'; target: PyExpr; iter: PyExpr; body: PyStmt[] }
  | { type: 'If'; test: PyExpr; body: PyStmt[]; orelse?: PyStmt[] }
  | { type: 'Try'; body: PyStmt[]; handlers: PyExceptHandler[] };

export type PyExceptHandler = {
  type: PyExpr;
  name?: string;
  body: PyStmt[];
};

export type PyKeyword = { arg?: string; value: PyExpr };

export const pyName = (id: string): PyExpr => ({ type: 'Name', id });
export const pyAttr = (value: PyExpr, attr: string): PyExpr => ({ type: 'Attribute', value, attr });
export const pyCall = (func: PyExpr, args: PyExpr[] = [], keywords: PyKeyword[] = []): PyExpr => ({
  type: 'Call',
  func,
  args,
  keywords,
});
export const pyConst = (value: string | number | boolean | null): PyExpr => ({ type: 'Constant', value });
export const pyDict = (items: { key: PyExpr; value: PyExpr }[] = []): PyExpr => ({ type: 'Dict', items });
export const pyList = (items: PyExpr[] = []): PyExpr => ({ type: 'List', items });
export const pyTuple = (items: PyExpr[] = []): PyExpr => ({ type: 'Tuple', items });
export const pySub = (value: PyExpr, index: PyExpr): PyExpr => ({ type: 'Subscript', value, index });
export const pyBin = (left: PyExpr, op: string, right: PyExpr): PyExpr => ({ type: 'BinOp', left, op, right });
export const pyCompare = (left: PyExpr, op: string, right: PyExpr): PyExpr => ({ type: 'Compare', left, op, right });

export const pyImport = (names: { name: string; as?: string }[]): PyStmt => ({ type: 'Import', names });
export const pyAssign = (targets: PyExpr[], value: PyExpr): PyStmt => ({ type: 'Assign', targets, value });
export const pyAugAssign = (target: PyExpr, op: string, value: PyExpr): PyStmt => ({
  type: 'AugAssign',
  target,
  op,
  value,
});
export const pyExpr = (value: PyExpr): PyStmt => ({ type: 'Expr', value });
export const pyFor = (target: PyExpr, iter: PyExpr, body: PyStmt[]): PyStmt => ({
  type: 'For',
  target,
  iter,
  body,
});
export const pyIf = (test: PyExpr, body: PyStmt[], orelse?: PyStmt[]): PyStmt => ({
  type: 'If',
  test,
  body,
  orelse,
});
export const pyTry = (body: PyStmt[], handlers: PyExceptHandler[]): PyStmt => ({
  type: 'Try',
  body,
  handlers,
});

const indentLines = (lines: string[], level: number): string[] => {
  const pad = ' '.repeat(level * 4);
  return lines.map((line) => (line.length ? `${pad}${line}` : line));
};

export const renderModule = (stmts: PyStmt[]): string => {
  return stmts.map((stmt) => renderStmt(stmt, 0)).join('\n') + '\n';
};

const renderExpr = (expr: PyExpr): string => {
  switch (expr.type) {
    case 'Name':
      return expr.id;
    case 'Attribute':
      return `${renderExpr(expr.value)}.${expr.attr}`;
    case 'Call': {
      const args = expr.args.map(renderExpr);
      const keywords = (expr.keywords ?? []).map((kw) =>
        kw.arg ? `${kw.arg}=${renderExpr(kw.value)}` : `**${renderExpr(kw.value)}`
      );
      return `${renderExpr(expr.func)}(${[...args, ...keywords].join(', ')})`;
    }
    case 'Constant':
      if (expr.value === null) return 'None';
      if (typeof expr.value === 'boolean') return expr.value ? 'True' : 'False';
      if (typeof expr.value === 'string') return JSON.stringify(expr.value);
      return String(expr.value);
    case 'Dict':
      if (expr.items.length === 0) return '{}';
      return `{${expr.items.map((item) => `${renderExpr(item.key)}: ${renderExpr(item.value)}`).join(', ')}}`;
    case 'List':
      return `[${expr.items.map(renderExpr).join(', ')}]`;
    case 'Tuple':
      return `(${expr.items.map(renderExpr).join(', ')})`;
    case 'Subscript':
      return `${renderExpr(expr.value)}[${renderExpr(expr.index)}]`;
    case 'BinOp':
      return `(${renderExpr(expr.left)} ${expr.op} ${renderExpr(expr.right)})`;
    case 'Compare':
      return `(${renderExpr(expr.left)} ${expr.op} ${renderExpr(expr.right)})`;
    default:
      return '';
  }
};

const renderStmt = (stmt: PyStmt, level: number): string => {
  switch (stmt.type) {
    case 'Import':
      return stmt.names
        .map((item) => `import ${item.name}${item.as ? ` as ${item.as}` : ''}`)
        .join('\n');
    case 'Assign':
      return `${stmt.targets.map(renderExpr).join(' = ')} = ${renderExpr(stmt.value)}`;
    case 'AugAssign':
      return `${renderExpr(stmt.target)} ${stmt.op}= ${renderExpr(stmt.value)}`;
    case 'Expr':
      return renderExpr(stmt.value);
    case 'For': {
      const header = `for ${renderExpr(stmt.target)} in ${renderExpr(stmt.iter)}:`;
      const body = stmt.body.length ? stmt.body : [pyExpr(pyName('pass'))];
      const rendered = body.map((child) => renderStmt(child, level + 1));
      return [header, ...indentLines(rendered, 1)].join('\n');
    }
    case 'If': {
      const header = `if ${renderExpr(stmt.test)}:`;
      const body = stmt.body.length ? stmt.body : [pyExpr(pyName('pass'))];
      const renderedBody = body.map((child) => renderStmt(child, level + 1));
      if (!stmt.orelse || stmt.orelse.length === 0) {
        return [header, ...indentLines(renderedBody, 1)].join('\n');
      }
      const elseBody = stmt.orelse.map((child) => renderStmt(child, level + 1));
      return [
        header,
        ...indentLines(renderedBody, 1),
        'else:',
        ...indentLines(elseBody, 1),
      ].join('\n');
    }
    case 'Try': {
      const header = 'try:';
      const body = stmt.body.length ? stmt.body : [pyExpr(pyName('pass'))];
      const renderedBody = body.map((child) => renderStmt(child, level + 1));
      const handlers = stmt.handlers.flatMap((handler) => {
        const exc = renderExpr(handler.type);
        const excName = handler.name ? ` as ${handler.name}` : '';
        const excHeader = `except ${exc}${excName}:`;
        const handlerBody = handler.body.length
          ? handler.body
          : [pyExpr(pyName('pass'))];
        const renderedHandler = handlerBody.map((child) => renderStmt(child, level + 1));
        return [excHeader, ...indentLines(renderedHandler, 1)];
      });
      return [header, ...indentLines(renderedBody, 1), ...handlers].join('\n');
    }
    default:
      return '';
  }
};
